import { useState } from "react";
import { ChevronLeft, ChevronRight, Download, Archive } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { downloadFile, photoSrc, errMsg } from "@/lib/api";
import { fmtStamp } from "@/lib/format";

export default function PhotoGallery({ photos, reportId, context }) {
  const [idx, setIdx] = useState(null);
  const cur = idx != null ? photos[idx] : null;
  const dl = (fn) => fn().catch((e) => toast.error(errMsg(e)));

  if (!photos.length) return <p className="text-sm text-slate-500" data-testid="gallery-empty">No photos attached.</p>;
  return (
    <div data-testid="photo-gallery">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">{photos.length} photo{photos.length === 1 ? "" : "s"}</p>
        <button className="btn-ghost h-10 text-sm" onClick={() => dl(() => downloadFile(`/reports/${reportId}/photos.zip`))} data-testid="download-all-photos-button">
          <Archive className="h-4 w-4" />Download all
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {photos.map((p, i) => (
          <button key={p.id} onClick={() => setIdx(i)} className="group text-left" data-testid={`gallery-photo-${p.id}`}>
            <span className="block aspect-square overflow-hidden rounded-md border border-border bg-slate-100">
              <img src={photoSrc(p.id)} alt={p.caption || p.filename} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
            </span>
            {p.caption && <span className="mt-1 line-clamp-2 block text-xs text-slate-600" data-testid={`gallery-caption-${p.id}`}>{p.caption}</span>}
          </button>
        ))}
      </div>
      <Dialog open={idx != null} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent className="max-w-5xl border-0 bg-slate-950 p-3 text-white sm:p-4" data-testid="photo-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {cur && (
            <div>
              <div className="relative flex max-h-[72vh] items-center justify-center">
                <img src={photoSrc(cur.id, "full")} alt={cur.filename} className="max-h-[72vh] w-auto max-w-full object-contain" data-testid="lightbox-image" />
                {idx > 0 && <button onClick={() => setIdx(idx - 1)} className="absolute left-1 flex h-11 w-11 items-center justify-center rounded-full bg-black/60" data-testid="lightbox-prev" aria-label="Previous"><ChevronLeft /></button>}
                {idx < photos.length - 1 && <button onClick={() => setIdx(idx + 1)} className="absolute right-1 flex h-11 w-11 items-center justify-center rounded-full bg-black/60" data-testid="lightbox-next" aria-label="Next"><ChevronRight /></button>}
              </div>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-xs text-white/70">
                  {cur.caption && <p className="mb-1 text-sm font-semibold text-white" data-testid="lightbox-caption">{cur.caption}</p>}
                  <p className="truncate font-semibold text-white/90">{cur.filename} · {idx + 1}/{photos.length}</p>
                  <p>{context} · Uploaded by {cur.uploaded_by_name} · {fmtStamp(cur.uploaded_at)} · {(cur.file_size / 1024 / 1024).toFixed(1)} MB</p>
                </div>
                <button className="inline-flex h-10 items-center gap-2 rounded-md bg-white px-4 text-sm font-semibold text-slate-900" onClick={() => dl(() => downloadFile(`/photos/${cur.id}`, { download: 1 }, cur.filename))} data-testid="lightbox-download-button">
                  <Download className="h-4 w-4" />Download
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
